import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import {
  NullablePriceAmountWithExchangeDetails,
  PriceAggregate,
  PriceAmountWithDetails,
} from '@/api';
import { DisplayPrice } from '@/components/PricesList/DisplayPrice';
import { EditPrice } from '@/components/PricesList/EditPrice';
import { PricesContainer } from '@/components/PricesList/PricesContainer';
import { PricesLine } from '@/components/PricesList/PricesLine';
import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog/Dialog';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { InputMoney } from '@/design_system/InputNumber/InputNumber';
import { OptionalLabel } from '@/design_system/Label/OptionalLabel';
import Stack from '@/design_system/Stack/Stack';
import { useUpdateArticleExchange } from '@/models/articleExchange';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import useViewPort from '@/utils/useViewport';

export const ServiceExchangePrice = () => (
  <PricesContainer>
    <TotalPricesLine />
  </PricesContainer>
);

const TotalPricesLine = () => {
  const { view: requestView } = useRequestContext();

  const showTotalLabel = requestView.workshopPrice.shown && requestView.organizationPrice.shown;

  return (
    <>
      <PricesLine
        label={
          showTotalLabel ? (
            <Trans id="article.form.articleExchange.price.total">Total</Trans>
          ) : requestView.workshopPrice.shown ? (
            requestView.workshopPrice.label
          ) : (
            requestView.organizationPrice.label
          )
        }
        fontWeight="medium"
        workshopPrice={<Price type="workshop" />}
        organizationPrice={<Price type="organization" />}
      />
    </>
  );
};

const Price = ({ type }: { type: 'workshop' | 'organization' }) => {
  const { article, state, view, errors } = useArticleContext();
  const { isMobile } = useViewPort();

  const articleExchange = article.snapshot.articleExchange!;
  const organizationPrice = getPrice(type, articleExchange.organizationPrice);
  const workshopPrice = getPrice(type, articleExchange.workshopPrice, article.workshopId);

  const price = type === 'workshop' ? workshopPrice : organizationPrice;

  const isEditing = !!state.priceUpdates.updatesInProgress.find(
    (priceUpdate) =>
      priceUpdate.articleExchangeId === articleExchange.id && priceUpdate.type === type
  );

  const isInvalid =
    type === 'workshop'
      ? errors.services?.exchange?.missingWorkshopPrice
      : errors.services?.exchange?.missingOrganizationPrice;

  const isEditable =
    !isMobile &&
    (type === 'workshop'
      ? view.services.exchange.workshopPrice.editable
      : view.services.exchange.organizationPrice.editable);

  const exchangeCatalogBreakdown =
    type === 'organization' ? getExchangeCatalogBreakdown(articleExchange) : undefined;

  if (isEditing) {
    return (
      <EditExchangePrice
        type={type}
        closeEditionMode={() => {
          state.priceUpdates.setUpdatesInProgress((prevState) =>
            prevState.filter(
              (priceUpdate) =>
                !(priceUpdate.articleExchangeId === articleExchange.id && priceUpdate.type === type)
            )
          );
        }}
      />
    );
  }

  return (
    <DisplayPrice
      line={'exchange'}
      type={type}
      fontWeight="medium"
      price={price}
      isEdited={false}
      isInvalid={isInvalid}
      isEditable={isEditable}
      customOrganizationPrice={articleExchange.customOrganizationPrice}
      exchangeCatalogBreakdown={exchangeCatalogBreakdown}
      isAnotherLineEditable={false}
      onPressEdit={() =>
        state.priceUpdates.setUpdatesInProgress((prevState) => [
          ...prevState,
          { articleExchangeId: articleExchange.id, type },
        ])
      }
    />
  );
};

const EditExchangePrice = ({
  type,
  closeEditionMode,
}: {
  type: 'workshop' | 'organization';
  closeEditionMode: () => void;
}) => {
  const { view: requestView } = useRequestContext();
  const { errors } = useArticleContext();
  const { currency, value, setValue, savePrice, label, isLoading } = usePriceEditionState({ type });

  const hasError =
    type === 'workshop'
      ? errors.services?.exchange?.workshopPriceUpdateInProgress
      : errors.services?.exchange?.organizationPriceUpdateInProgress;

  if (!currency) {
    return null;
  }

  return (
    <EditPrice
      closeEditionMode={closeEditionMode}
      label={label}
      currency={currency}
      value={value}
      setValue={setValue}
      savePrice={savePrice}
      isLoading={isLoading}
      hasError={hasError}
      allowNegativeAmount={type === 'organization' && requestView.organizationPrice.negativeEnabled}
    />
  );
};

export const EditExchangeMobile = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) => {
  const { t } = useLingui();

  const title = t({ id: 'edit-exchange-dialog.title', message: 'Edit article exchange' });

  return (
    <Dialog
      title={title}
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        setIsOpen(newIsOpen);
      }}
    >
      <MobileEditForm onSave={() => setIsOpen(false)} />
    </Dialog>
  );
};

const MobileEditForm = ({ onSave }: { onSave: () => void }) => {
  const { view: requestView } = useRequestContext();
  const { view } = useArticleContext();

  const showWorkshopPrice =
    requestView.workshopPrice.shown && view.services.exchange.workshopPrice.editable;
  const showOrganizationPrice =
    requestView.organizationPrice.shown && view.services.exchange.organizationPrice.editable;

  return (
    <>
      <main>
        <Stack gap="1rem">
          {showWorkshopPrice && <MobilePriceInput type="workshop" />}
          {showOrganizationPrice && <MobilePriceInput type="organization" />}
        </Stack>
      </main>
      <Footer>
        <FooterButtons>
          <Button variant="primary" onPress={onSave}>
            <Trans id="edit-exchange-dialog.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};

const MobilePriceInput = ({ type }: { type: 'workshop' | 'organization' }) => {
  const { view: requestView } = useRequestContext();
  const { errors } = useArticleContext();

  const { value, setValue, currency, savePrice, initialValue, label, isRequired, isLoading } =
    usePriceEditionState({
      type,
    });

  const isInvalid =
    type === 'workshop'
      ? errors.services?.exchange?.missingWorkshopPrice
      : errors.services?.exchange?.missingOrganizationPrice;

  if (!currency) {
    return null;
  }

  const handleValueChange = async (newValue: number) => {
    setValue(newValue);

    try {
      await savePrice(newValue);
    } catch {
      setValue(initialValue);
    }
  };

  return (
    <InputMoney
      label={
        <>
          {label}
          {!isRequired && <OptionalLabel />}
        </>
      }
      placeholder="0"
      value={value ?? NaN}
      onChange={handleValueChange}
      currency={currency}
      isInvalid={isInvalid}
      isLoading={isLoading}
      allowNegativeAmount={type === 'organization' && requestView.organizationPrice.negativeEnabled}
    />
  );
};

const getPrice = (
  type: 'workshop' | 'organization',
  priceAggregate?: PriceAggregate | null,
  workshopId?: string | null
): PriceAmountWithDetails | undefined => {
  return type === 'workshop'
    ? priceAggregate?.amountPerEntity?.find((amount) => amount.entityId === workshopId)
    : priceAggregate?.amountPerEntity?.[0];
};

const getExchangeCatalogBreakdown = (articleExchange: {
  customOrganizationPrice?: { amount?: number | null } | null;
  catalogOrganizationPrice?: NullablePriceAmountWithExchangeDetails | null;
}): { replacementAmountBeforeTaxes: number; currentAmountBeforeTaxes: number } | undefined => {
  if (
    articleExchange.customOrganizationPrice &&
    articleExchange.customOrganizationPrice.amount !== null
  ) {
    return undefined;
  }

  const catalogOrganizationPrice = articleExchange.catalogOrganizationPrice;

  if (!catalogOrganizationPrice) {
    return undefined;
  }

  const retailPriceExchangedItem = catalogOrganizationPrice.retailPriceExchangedItem;
  const retailPriceReplacementItem = catalogOrganizationPrice.retailPriceReplacementItem;

  if (!retailPriceExchangedItem?.amount || !retailPriceReplacementItem?.amount) {
    return undefined;
  }

  const taxRate = catalogOrganizationPrice.taxes[0]?.rate ?? 0;
  const noVATApplied = catalogOrganizationPrice.noVATApplied;

  const toAmountBeforeTaxes = (amount: number) =>
    noVATApplied ? amount : Math.round((amount * 100) / (100 + taxRate));

  return {
    replacementAmountBeforeTaxes: toAmountBeforeTaxes(retailPriceReplacementItem.amount),
    currentAmountBeforeTaxes: toAmountBeforeTaxes(retailPriceExchangedItem.amount),
  };
};

const usePriceEditionState = ({ type }: { type: 'workshop' | 'organization' }) => {
  const { view: requestView } = useRequestContext();
  const { article, view } = useArticleContext();

  const showAmountBeforeTaxes =
    type === 'workshop'
      ? requestView.workshopPrice.showAmountBeforeTaxes
      : requestView.organizationPrice.showAmountBeforeTaxes;

  const customFieldName = type === 'workshop' ? 'customWorkshopPrice' : 'customOrganizationPrice';

  const customPrice = article.snapshot?.articleExchange?.[customFieldName];

  const currency = customPrice?.currency || 'EUR';

  const initialValue = showAmountBeforeTaxes ? customPrice?.amountBeforeTaxes : customPrice?.amount;

  const [value, setValue] = useState(initialValue ?? undefined);

  const { mutateAsync: updateExchange, isPending: isUpdateExchangePending } =
    useUpdateArticleExchange(article.id);

  const label =
    type === 'workshop' ? requestView.workshopPrice.label : requestView.organizationPrice.label;
  const isRequired =
    type === 'workshop'
      ? view.services.exchange.workshopPrice.required
      : view.services.exchange.organizationPrice.required;

  const savePrice = async (newValue: number | undefined) => {
    await updateExchange({
      [customFieldName]:
        newValue !== undefined && !isNaN(newValue)
          ? showAmountBeforeTaxes
            ? { amountBeforeTaxes: newValue }
            : { amount: newValue }
          : null,
    });
  };

  return {
    value,
    setValue,
    currency,
    savePrice,
    initialValue: initialValue ?? undefined,
    label,
    isRequired,
    isLoading: isUpdateExchangePending,
  };
};
